// STX-PROD-02: one-click entry (Product Readiness criterion 2).
//
// The MSAL cache is sessionStorage by a security decision (S11 [SEC-D]: tokens don't outlive the tab),
// so every NEW tab starts with no account, and a cyclical user returning after a quiet fortnight landed
// on "Sign in" every time. ssoSilent asks Microsoft, in a hidden iframe, whether this browser already
// has a signed-in PTC session; if it does, the person is in without a click and the cache policy is
// unchanged.
//
// Exactly ONE attempt per tab session, recorded BEFORE it is made: App renders a spinner while MSAL
// reports the attempt in progress and remounts the login page when it ends, so an unguarded attempt
// would retry forever. If the attempt can't be recorded, it isn't made. A failure (no Microsoft
// session, third-party cookies blocked, several sessions and no hint) just leaves today's button.

import type { AccountInfo, IPublicClientApplication } from '@azure/msal-browser';
import { apiScopes } from '../config/authConfig';

/** sessionStorage: set once the attempt is made in this tab. */
export const SILENT_ATTEMPT_KEY = 'statetax.auth.silentAttempted';
/** localStorage: the username of the last person who signed in on this browser (a hint, not a credential). */
export const LOGIN_HINT_KEY = 'statetax.auth.loginHint';

/** Remembers who signed in, so the next silent attempt can name them when the browser holds several Microsoft sessions. */
export function rememberLoginHint(account: AccountInfo | null | undefined): void {
  if (!account?.username) return;
  try { localStorage.setItem(LOGIN_HINT_KEY, account.username); } catch { /* a hint is optional */ }
}

export function shouldAttemptSilentSignIn(): boolean {
  try { return sessionStorage.getItem(SILENT_ATTEMPT_KEY) === null; } catch { return false; }
}

/** Resolves true when the person is now signed in; false otherwise. Never throws. */
export async function attemptSilentSignIn(instance: IPublicClientApplication): Promise<boolean> {
  try {
    sessionStorage.setItem(SILENT_ATTEMPT_KEY, String(Date.now()));
  } catch {
    return false;   // can't guarantee "once": don't try at all
  }
  let loginHint: string | null = null;
  try { loginHint = localStorage.getItem(LOGIN_HINT_KEY); } catch { /* no hint */ }

  try {
    const result = await instance.ssoSilent(loginHint ? { scopes: apiScopes, loginHint } : { scopes: apiScopes });
    if (result.account) {
      instance.setActiveAccount(result.account);
      rememberLoginHint(result.account);
    }
    return !!result.account;
  } catch {
    return false;
  }
}
